import { saveAs } from 'file-saver'
import { EMPTY, Observable, of, Subscription } from 'rxjs'
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators'
import { environment } from 'src/environments/environment'
import * as THREE from 'three'

import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { S3ObjectRef } from '@classes/FeatureProperty'
import { FileReference } from '@classes/FileReference'

import { AuthenticationService } from './authentication.service'
import { AwsService, UploadPurpose } from './aws.service'
import { project$, ProjectService } from './project.service'
import { EndpointOptions, RequestService } from './request.service'
import { ToastService } from './toast.service'

@Injectable({
    providedIn: 'root'
})
export class FileReferenceService {

    constructor(
        private _authenticationService: AuthenticationService,
        private _awsService: AwsService,
        private _http: HttpClient,
        private _projectService: ProjectService,
        private _requestService: RequestService,
        private _toastService: ToastService,
    ) { }

    /**
     * Based on the loaded module and/or if the project is public
     * @returns The url for downloading a file
     */
    getUrlForFileDownload(fileReferenceID: number, modelProjectOrAssetID: number, hash: string, token?: string, fileReference?: FileReference, contextOverride?: 'project' | 'public' | 'model' | 'asset') {
        const isForModels = this._authenticationService.currentModule == 'model-editor' || this._authenticationService.currentModule == 'models'
        const isViewer = this._authenticationService.currentModule == 'viewer'
        const isPublic = this._projectService.currentProject?.publicStatus
        const isForAssets = this._authenticationService.currentModule == 'assets'
        var context = contextOverride ?? 'project'

        if (contextOverride != null) {
            context = contextOverride
        } else if (isViewer && isPublic) {
            context = 'public'
        } else if (isForModels) {
            context = 'model'
        } else if (isForAssets) {
            context = 'asset'
        }

        const url = `${environment.api}/file/${context}/${fileReferenceID}/${modelProjectOrAssetID}/${hash}`
        return token ? `${url}?token=${token}` : url
    }

    createAssetFieldValueFileReferences(fieldValueID: number, files: File[], options: { label?: string, title?: string, autohide?: boolean } = {}) {
        var url = `${environment.api}/files/assetFieldValue/${fieldValueID}`
        const formData: FormData = new FormData()

        if (options?.label) formData.append('label', options.label.toString())
        files.forEach(file => formData.append('file', file, file.name))

        const endpointOptions: EndpointOptions = {
            progressBar: {
                color: 'blue',
                autohide: options?.autohide ?? false,
                title: options?.title ?? `Uploading Files`
            },
            successToast: {
                color: 'green',
                autohide: true,
                title: 'File Reference Created'
            },
            error: {
                operation: 'Create File Reference',
                toast: true
            }
        }
        return this._requestService.create<FileReference[]>(url, formData, endpointOptions)

    }

    /** Uploads files to S3 and links them to an asset field value via the S3 junction table */
    createAssetFieldValueS3Files(fieldValueID: number, files: File[], options: { label?: string } = {}) {
        const url = `${environment.api}/s3/assetFieldValue/${fieldValueID}`

        return this._awsService.uploadAndRegisterMultiple(files, 'upload-asset-file', (f, i) => options?.label ?? f?.name).pipe(
            switchMap(s3Refs => {
                const s3ObjectReferenceIds = s3Refs.map(r => r.id)
                const endpointOptions: EndpointOptions = {
                    successToast: { color: 'green', autohide: true, title: 'Files Uploaded' },
                    error: { operation: 'Upload S3 Files', toast: true }
                }
                return this._requestService.create(url, { s3ObjectReferenceIds }, endpointOptions).pipe(
                    map(() => s3Refs)
                )
            })
        )
    }


    downloadFile(
        fileReference: FileReference,
        modelProjectOrAssetID: number,
        headers: { [header: string]: string | string[] } = {},
        contextOverride?: 'project' | 'public' | 'model' | 'asset'
    ) {
        if (!fileReference) return EMPTY

        return this._authenticationService.getToken().pipe(
            switchMap(token => {
                const { id: fileReferenceID, hash } = fileReference
                const endpoint = this.getUrlForFileDownload(
                    fileReferenceID,
                    modelProjectOrAssetID,
                    hash,
                    token,
                    fileReference,
                    contextOverride
                )

                return this.createBlob(endpoint, { authorization: `Bearer ${token}` }).pipe(
                    filter(blob => blob !== undefined),
                    tap(blob => saveAs(blob, fileReference.filename))
                )
            })
        )
    }

    downloadPointCloudFile(fileReference: FileReference) {
        const url = `${environment.api}/file/model/${fileReference.id}/${fileReference.modelID}/${fileReference.hash}`
        return this._authenticationService.getToken().pipe(
            switchMap(token => {
                return this._http.get(url, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' }).pipe(
                    map(blob => URL.createObjectURL(blob)),
                    tap(blobUrl => {
                        saveAs(blobUrl, fileReference.filename)
                        URL.revokeObjectURL(blobUrl)
                    })
                )
            })
        )
    }

    /** Downloads a file using its S3 object reference ID.
     *  This is the canonical entry point for S3 file downloads in feature/connection/scene contexts.
     *  For model file downloads by bucket+key, use AwsService.downloadS3ModelFile() directly. */
    downloadS3File(s3ObjectReferenceId: number, filename?: string) {
        return this._awsService.downloadS3FileById(s3ObjectReferenceId, filename)
    }

    /** Returns a presigned download URL for an S3 object reference.
     *  This is the canonical entry point for getting S3 presigned URLs in feature/connection/scene contexts. */
    getS3PresignedUrl(s3ObjectReferenceId: number): Observable<string> {
        return this._awsService.getPresignedDownloadUrl(s3ObjectReferenceId).pipe(
            map(({ url }) => url)
        )
    }

    /** Returns a presigned URL for an S3 file, using the public endpoint when in viewer mode on a public project.
     *  Falls back to the authenticated presigned URL otherwise. */
    getS3FileUrlForProject(s3ObjectReferenceId: number): Observable<string> {
        const isViewer = this._authenticationService.currentModule == 'viewer'
        const isPublic = this._projectService.currentProject?.publicStatus

        if (isViewer && isPublic) {
            const projectId = this._projectService.currentProject.id
            return of(`${environment.api}/public/s3/file-stream/${s3ObjectReferenceId}/${projectId}`)
        }

        return this.getS3PresignedUrl(s3ObjectReferenceId)
    }

    createBlob(url: string, headers: { [header: string]: string | string[] } = {}) {
        return this._http.get(url, { headers, responseType: 'blob' }).pipe(
            catchError(() => this._http.get(url, { responseType: 'blob' })),
            map(blob => URL.createObjectURL(blob))
        )
    }

    blobFromS3Reference(s3ObjectReferenceId: number): Observable<string> {
        return this.getS3FileUrlForProject(s3ObjectReferenceId).pipe(
            switchMap(url => this._http.get(url, { responseType: 'blob' })),
            map(blob => URL.createObjectURL(blob))
        )
    }

    blobFromFileReference(fileReference: FileReference, interrupt$: Observable<any>) {
        return this._authenticationService.getToken().pipe(
            withLatestFrom(project$),
            switchMap(([token, project]) => {
                const { id: fileReferenceID, hash } = fileReference
                const projectID = project.id
                const fileURL = this.getUrlForFileDownload(fileReferenceID, projectID, hash, token)


                if (interrupt$) return this.loadFileWithInterrupt(fileURL, token, interrupt$)
                else return new Observable<string>(observer =>
                    new THREE.FileLoader()
                        .setResponseType('blob')
                        .setRequestHeader({ 'Authorization': `Bearer ${token}` })
                        .load(fileURL, (response: any) => {
                            const blobUrl: string = URL.createObjectURL(response)
                            observer.next(blobUrl)
                            observer.complete()
                        })
                )
            })
        )
    }

    loadFileWithInterrupt(fileURL: string, token: string, interrupt$: Observable<boolean>): Observable<string> {
        return new Observable((observer) => {
            const xhr = new XMLHttpRequest()
            xhr.responseType = 'blob'
            xhr.open('GET', fileURL, true)
            xhr.setRequestHeader('Authorization', `Bearer ${token}`)

            xhr.onload = () => {
                if (xhr.status === 200) {
                    const blob = xhr.response
                    const blobURL = URL.createObjectURL(blob)
                    observer.next(blobURL)
                    observer.complete()
                    interruptSubscription.unsubscribe()
                } else {
                    observer.error('Load failed with status: ' + xhr.status)
                }
            }

            xhr.onerror = () => {
                observer.error('Network error')
            }

            xhr.send()

            const interruptSubscription = interrupt$.subscribe(_ => {
                xhr.abort()
                observer.error('Request aborted')
                interruptSubscription.unsubscribe()
            })

        })
    }


    openFile(fileReference: FileReference, modelProjectOrAssetID: number, contextOverride?: 'project' | 'public' | 'model' | 'asset'): Observable<any> {
        const imageTypes = ['.jpg', '.jpeg', '.png']
        const fileIsImage = imageTypes.includes(fileReference.extension)
        const fileIsPDF = fileReference.extension == '.pdf'

        if (fileIsImage) {
            return this.openImageInNewTab(fileReference, modelProjectOrAssetID, contextOverride)
        } else if (fileIsPDF) {
            return this.openPdfInNewTab(fileReference, modelProjectOrAssetID, contextOverride)
        } else {
            return this.downloadFile(fileReference, modelProjectOrAssetID, {}, contextOverride)
        }
    }

    openImageInNewTab(fileReference: FileReference, modelProjectOrAssetID: number, contextOverride?: 'project' | 'public' | 'model' | 'asset') {
        return this.getUrlForFileReference(fileReference, modelProjectOrAssetID, contextOverride).pipe(
            tap(url => {
                // Create a new window/tab
                const newWindow = window.open('', '_blank')

                // Write content to the new window/tab
                newWindow.document.write(`<html><head><title>${fileReference.filename}</title></head><body><img src="${url}" style='max-width:500px;'></body></html>`)

                // Close the document to allow interaction
                newWindow.document.close()
            })
        )
    }

    getUrlForFileReference(fileReference: FileReference, modelProjectOrAssetID: number, contextOverride?: 'project' | 'public' | 'model' | 'asset') {
        return this._authenticationService.getToken().pipe(
            map(token => {
                return this.getUrlForFileDownload(fileReference.id, modelProjectOrAssetID, fileReference.hash, token, fileReference, contextOverride)
            })
        )
    }

    /** Loads an authenticated EFS file and returns a browser-safe object URL for image previews. */
    getBlobUrlForFileReference(fileReference: FileReference, modelProjectOrAssetID: number, contextOverride?: 'project' | 'public' | 'model' | 'asset') {
        return this._authenticationService.getToken().pipe(
            switchMap(token => {
                const url = this.getUrlForFileDownload(
                    fileReference.id,
                    modelProjectOrAssetID,
                    fileReference.hash,
                    undefined,
                    fileReference,
                    contextOverride
                )

                return this._http.get(url, {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob'
                })
            }),
            map(blob => URL.createObjectURL(blob))
        )
    }

    openPdfInNewTab(fileReference: FileReference, modelProjectOrAssetID: number, contextOverride?: 'project' | 'public' | 'model' | 'asset') {
        return this.getUrlForFileReference(fileReference, modelProjectOrAssetID, contextOverride).pipe(
            switchMap(url => {
                return this._http.get(url, { responseType: 'blob' }).pipe(
                    map(res => new Blob([res], { type: 'application/pdf' })),
                    catchError(error => {
                        console.error('Error fetching PDF:', error)
                        return EMPTY
                    })
                )
            }),
            tap(blob => {
                const blobURL = URL.createObjectURL(blob)
                window.open(blobURL, '_blank')
            })
        )
    }

    async resizeImage(inputImage: HTMLImageElement, imageFile: File): Promise<File> {
        // Create a canvas element
        const canvas = document.createElement('canvas')
        const context = canvas.getContext('2d')

        canvas.width = inputImage.width
        canvas.height = inputImage.height

        // Set the canvas dimensions to the new size
        if (canvas.width == canvas.height) {
            canvas.width = 1024
            canvas.height = 1024
        } else if (canvas.width > canvas.height) {
            let ratio = canvas.height / canvas.width
            canvas.width = 1024
            canvas.height = 1024 * ratio
        } else if (canvas.height > canvas.width) {
            let ratio = canvas.width / canvas.height
            canvas.width = 1024 * ratio
            canvas.height = 1024
        }

        // Draw the image on the canvas with the new dimensions
        context.drawImage(inputImage, 0, 0, canvas.width, canvas.height)

        // Return the resized image as a canvas element
        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                if (blob) {
                    // Create a File from the Blob
                    const file = new File([blob], imageFile.name, { type: imageFile.type })

                    // Resolve the Promise with the resized File
                    resolve(file)
                }
            }, imageFile.type, 0.8)
        })
    }

    /** Resizes an image if needed, uploads to S3, and registers it. Handles blob URL lifecycle. */
    public uploadImageAndRegister(file: File, purpose: UploadPurpose, label = 'image'): Observable<S3ObjectRef> {
        return new Observable<S3ObjectRef>(observer => {
            const image = new Image()
            const blobUrl = URL.createObjectURL(file)
            let innerSub: Subscription | undefined
            image.src = blobUrl

            image.onload = async () => {
                URL.revokeObjectURL(blobUrl)

                let processedFile = file
                if (image.width > 1024 || image.height > 1024) {
                    processedFile = await this.resizeImage(image, file)
                }

                innerSub = this._awsService.uploadAndRegister(processedFile, purpose, label).subscribe({
                    next: ref => observer.next(ref),
                    error: err => observer.error(err),
                    complete: () => observer.complete()
                })
            }

            image.onerror = () => {
                URL.revokeObjectURL(blobUrl)
                observer.error(new Error(`Failed to load image: ${file.name}`))
            }

            return () => innerSub?.unsubscribe()
        })
    }

    /**
     * Handle Http operation that failed.
     * Let the app continue.
     * @param operation - name of the operation that failed
     * @param result - optional value to return as the observable result
     */
    private handleError<T>(operation = 'Operation did not complete.', result?: T) {
        return (error: any): Observable<T> => {

            this._toastService.toast({ title: "Error", message: operation, color: "red" })

            // TODO: send the model error to remote logging infrastructure
            console.error(error); // log to console instead

            // TODO: better job of transforming error for user consumption
            // this.log(`${operation} failed: ${error.message}`);

            // Let the app keep running by returning an empty result.
            return of(result as T);
        };
    }
}
